import React from "react";
import { useSelectContextProvider } from "./SelectContextProvider";
import VirtualList from "react-tiny-virtual-list";
import { Checkmark16 } from "@carbon/icons-react";
import { SelectMenuItem } from "./SelectMenuItem";

export const SelectMenuItems = <T,>({ itemHeight }: { itemHeight: number }): JSX.Element => {
  const {
    filteredOptions,
    options,
    query,
    optionTemplate,
    onChange,
    cursor,
    dropdownHeight,
    value,
    disabledFilter,
  } = useSelectContextProvider();

  const data = filteredOptions.length > 0 ? filteredOptions : options || [];

  // Options carrying a `group` render a small category header above the
  // first option of each consecutive group.
  const GROUP_HEADER_HEIGHT = 28;

  const startsGroup = (index: number) =>
    Boolean(data[index]?.group) &&
    (index === 0 || data[index - 1]?.group !== data[index]?.group);

  const rowHeight = (index: number) =>
    startsGroup(index) ? itemHeight + GROUP_HEADER_HEIGHT : itemHeight;

  const totalHeight = data.reduce((sum, _, index) => sum + rowHeight(index), 0);

  return (
    <>
      {query !== "" && filteredOptions.length === 0 ? (
        <div className="p-3 text-sm leading-none text-center text-gray-400">
          No results for {query}
        </div>
      ) : (
        <VirtualList
          width="100%"
          height={Math.min(totalHeight, dropdownHeight)}
          itemCount={data.length}
          itemSize={rowHeight}
          renderItem={({ index, style }) => {
            const option = data[index];

            const isDisabled = disabledFilter ? disabledFilter(option) : false;

            const menuItem = (innerStyle?: React.CSSProperties) => (
              <SelectMenuItem
                key={index}
                style={innerStyle}
                index={index}
                onClick={() => {
                  onChange && onChange(option);
                }}
                isDisabled={isDisabled}
                isFocused={index === cursor}
              >
                {optionTemplate
                  ? React.createElement(optionTemplate, { ...option })
                  : option.label}

                {option?.value === value?.value && (
                  <div className="ml-auto text-green-500">
                    <Checkmark16 />
                  </div>
                )}
              </SelectMenuItem>
            );

            if (!startsGroup(index)) {
              return menuItem(style);
            }

            return (
              <div key={index} style={style}>
                <div
                  className="flex items-center px-3 text-xs font-semibold tracking-wide uppercase bg-gray3 text-gray11 select-none"
                  style={{ height: GROUP_HEADER_HEIGHT }}
                >
                  {option.group}
                </div>
                {menuItem({ height: itemHeight })}
              </div>
            );
          }}
        />
      )}
    </>
  );
};
